import { Form } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useStore } from 'store/useStore'
import { CountryItem } from 'components/CountryPhoneInput/CountryPhoneInput'
import { Margin } from 'components/ui'
import { Href } from 'components/ui/Href/Href'
import { ReactSelect } from 'components/ui/Select/ReactSelect'
import { Spinner } from 'components/ui/Spinner/Spinner'
import { ErrorText } from 'components/ui/Text/Text'
import { UploadFile } from 'components/ui/UploadFile/UploadFile'
import { Checkbox, Divider, FormItem, Input, Paragraph, Text } from 'components/uiKit'
import { isGeDomain } from 'utils/helpers/domains/getDomain'
import { ConvertedFile } from 'utils/helpers/files/fileUpload'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
// import { PromoRoutesEnum } from 'shared/constants/routes'
import { URLS } from 'shared/constants/urls'
import { colors } from 'shared/constants/values'
import { BaseOption, Country } from 'shared/types/types'

import { accountDetails, companyNameWithCompanyType, countries } from '../../../data'
import { CompanyDetailsViewModel } from '../../../stores/models/CompanyDetailsViewModel'
import { CompanyType, TaxCheckStatusType, TaxTypes, UserModuleKeys } from '../../../types'
import { useSendTaxFile } from '../../../utils/useSendTaxFile'
import { accountPlacehorders } from './data'

type ActivityStepProps = {
  companyDetails: CompanyDetailsViewModel
  isEdit?: boolean
}

const MIN_PAY_ACCOUNT_FOR_ALL_COUNTRY = 19
const MIN_BIK_FOR_ALL_COUNTRY = 8

export const ActivityStep = observer(({ companyDetails, isEdit = false }: ActivityStepProps) => {
  const { appStore } = useStore()
  const trySendTaxFile = useSendTaxFile()
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [isFilesEmpty, setFilesEmpty] = useState<boolean>(false)

  const getKey = (key: string) => `${UserModuleKeys.activityStep}.${key}`
  const getDataKey = (key: string) => `${UserModuleKeys.data}.${key}`
  const selectedCountry = companyDetails.selectedCountry.country
  const taxType = companyDetails.taxType
  const isTaxCheckStatusVisible =
    (companyDetails.hasTaxFiles ||
      (!companyDetails.hasTaxFiles && companyDetails.taxCheckStatus !== TaxCheckStatusType.review)) &&
    isEdit
  const innLabel = companyDetails.innLable
  const isBikOrPayAccountInvalid =
    companyDetails.payAccount.length > MIN_PAY_ACCOUNT_FOR_ALL_COUNTRY &&
    companyDetails.bik.length > MIN_BIK_FOR_ALL_COUNTRY &&
    companyDetails.isAnBikInvalid

  const taxTooltip =
    isEdit && selectedCountry === Country.RU ? (
      <Text style={{ color: 'white' }}>
        Обратите внимание, что система налогообложения будет изменена после проверки подтверждающего документа. Проверка
        занимает 1-2 дня.
      </Text>
    ) : (
      ''
    )

  useEffect(() => {
    if (taxType === TaxTypes.osn) {
      setFilesEmpty(true)
    } else {
      setFilesEmpty(false)
    }
  }, [taxType])

  const handleInnChange = (textDirty: string) => {
    const text = textDirty.replace(/[^\d]/g, '')
    if (companyDetails.selectedCountry.country === Country.RU) {
      if (text.length === companyDetails.innMaxLength) {
        companyDetails.updateFields(text)
      }
    } else {
      companyDetails.setInn(text)
    }

    companyDetails.setInnText(text)
  }

  const handlePressText = (newCompanyType: CompanyType) => () => {
    if (companyDetails.companyType && newCompanyType !== companyDetails.companyType) {
      companyDetails.clearCompanyInfo()
      form.resetFields()
    }

    companyDetails.setCompanyType(newCompanyType)
  }

  const handleTaxType = (type: TaxTypes) => {
    companyDetails.setTaxType(type)
    handleDeleteFile()
  }

  const handleFile = async (file: ConvertedFile) => {
    if (companyDetails.loading) return

    const result = await trySendTaxFile(file, appStore.user.info.user_id)

    if (result) {
      companyDetails.setTaxFiles([result.imageUrl])
      companyDetails.setTaxFilesUI([file.content])
      setFilesEmpty(false)
    } else {
      handleDeleteFile()
      setFilesEmpty(true)
    }

    companyDetails.setLoading(false)
  }

  const handleDeleteFile = () => {
    companyDetails.setTaxFiles([])
    companyDetails.setTaxFilesUI([])
    if (taxType === TaxTypes.osn) {
      setFilesEmpty(true)
    } else {
      setFilesEmpty(false)
    }
  }

  const handleSelectedCountry = (item: BaseOption<CountryItem>) => {
    companyDetails.setSelectedCountry(item.value ?? countries[0].value)
    companyDetails.clearActivityStep()
    form.resetFields()
  }

  const localizedCountries = useMemo(() => {
    return countries.map((country) => ({
      ...country,
      label: t(getSharedCountryKey(country.value.label)),
    }))
  }, [t])

  const localizedCountry = (value: BaseOption<CountryItem>) => {
    return {
      ...value,
      label: t(getSharedCountryKey(value.label)),
    }
  }

  function getSharedCountryKey(key: string) {
    return `${UserModuleKeys.countries}.${key}`
  }

  const getStatusColor = (status: TaxCheckStatusType) => {
    switch (status) {
      case TaxCheckStatusType.review:
        return colors.secondary
      case TaxCheckStatusType.remark:
        return colors.orange
      case TaxCheckStatusType.canceled:
        return colors.red
      case TaxCheckStatusType.success:
        return colors.green
    }
  }

  useEffect(() => {
    form.setFieldValue('innText', companyDetails.innText)
    form.setFieldValue('companyName', companyDetails.companyName)
    form.setFieldValue('bankName', companyDetails.bankName)
    form.setFieldValue('bik', companyDetails.bik)
    form.setFieldValue('payAccount', companyDetails.payAccount)
    form.setFieldValue('legalAddress', companyDetails.legalAddress)
    form.setFieldValue('corAccount', companyDetails.corAccount)
    form.setFieldValue('gln', companyDetails.gln)
  }, [
    companyDetails.innText,
    companyDetails.companyName,
    companyDetails.bankName,
    companyDetails.bik,
    companyDetails.payAccount,
    companyDetails.legalAddress,
    companyDetails.corAccount,
    companyDetails.gln,
  ])

  const getInnPlaceholder = () => {
    const innPlaceholder = accountPlacehorders[selectedCountry].inn

    if (typeof innPlaceholder === 'object') {
      return innPlaceholder[companyDetails.companyType]
    }

    return innPlaceholder
  }

  const bikNormalize = (value: string) => {
    if (
      selectedCountry === Country.AM ||
      selectedCountry === Country.KZ ||
      selectedCountry === Country.BY ||
      selectedCountry === Country.GE
    ) {
      return value
    }
    return value.replace(/[^\d]/g, '')
  }

  const normalizePayAccount = (value: string) => {
    if (selectedCountry === Country.GE || selectedCountry === Country.BY || selectedCountry === Country.KZ) {
      return value
    }
    return value.replace(/[^\d]/g, '')
  }

  return (
    <Form form={form} layout="vertical" style={{ maxWidth: 600, marginTop: 50 }}>
      {!isEdit && <Paragraph>{t(getKey('Необходимо выбрать страну'))}.</Paragraph>}

      <FormItem label={t(getKey('Страна'))}>
        <ReactSelect
          isDisabled={isEdit}
          placeholder={t(getKey('Страна'))}
          options={localizedCountries}
          value={localizedCountry(countries.find((value) => value.value.country === selectedCountry) ?? countries[0])}
          onChange={(item) => handleSelectedCountry(item as BaseOption<CountryItem>)}
        />
      </FormItem>

      <FormItem label={t(getKey('Форма деятельности'))}>
        {companyNameWithCompanyType[selectedCountry].map(([label, type]) => (
          <Checkbox
            key={type}
            checked={companyDetails.companyType === type}
            onClick={handlePressText(type as CompanyType)}
            disabled={isEdit}
          >
            {t(getDataKey(label))}
          </Checkbox>
        ))}
      </FormItem>

      <FormItem label={t(getKey('Налогообложение'))} tooltip={taxTooltip}>
        <Checkbox checked={taxType === TaxTypes.usn} onClick={() => handleTaxType(TaxTypes.usn)}>
          {t(getKey('Без НДС'))}
        </Checkbox>
        <Checkbox
          checked={taxType === TaxTypes.osn}
          onClick={() => handleTaxType(TaxTypes.osn)}
          disabled={companyDetails.onlyTaxUsnCountry}
        >
          {t(getKey('НДС'), { count: 20 })}%
        </Checkbox>

        <Checkbox
          checked={taxType === TaxTypes.lowerRate5}
          onClick={() => handleTaxType(TaxTypes.lowerRate5)}
          disabled={companyDetails.onlyTaxUsnCountry || selectedCountry !== Country.RU}
        >
          {t(getKey('НДС'), { count: 5 })}%
        </Checkbox>
        <Checkbox
          checked={taxType === TaxTypes.lowerRate7}
          onClick={() => handleTaxType(TaxTypes.lowerRate7)}
          disabled={companyDetails.onlyTaxUsnCountry || selectedCountry !== Country.RU}
        >
          {t(getKey('НДС'), { count: 7 })}%
        </Checkbox>
      </FormItem>

      <FormItem
        name={'taxFile'}
        label={
          taxType === TaxTypes.usn
            ? `${t(getKey('Если вы являетесь плательщиком «БЕЗ НДС»'))}(${t(getSharedKey('notRequired'))})`
            : `${t(getKey('Если вы являетесь плательщиком НДС'))}(${t(getSharedKey('required'))})`
        }
        rules={[{ required: taxType === TaxTypes.osn, message: t(getSharedKey('required')) }]}
      >
        <UploadFile
          onChange={handleFile}
          contentUrl={companyDetails.taxFilesUI[0]}
          onDelete={handleDeleteFile}
          disabled={companyDetails.loading}
        />
        {isFilesEmpty && <ErrorText error>{t(getKey('Прикрепите документ'))}</ErrorText>}
      </FormItem>

      <Margin mt={20}>
        <Paragraph>{t(getKey('Прикрепите файл в формате'))}</Paragraph>
      </Margin>

      {isTaxCheckStatusVisible && (
        <Margin mt={20}>
          <Paragraph>
            - {t(getKey('Статус проверки НДС'))}:{' '}
            <Text style={{ color: getStatusColor(companyDetails.taxCheckStatus) }}>
              {companyDetails.taxCheckStatusName}
            </Text>
          </Paragraph>
          {companyDetails.taxCheckStatus === TaxCheckStatusType.remark && (
            <Paragraph>
              {' '}
              - {t(getKey('Комментарий от менеджера'))}: {companyDetails.taxCheckStatusComment}
            </Paragraph>
          )}
          {companyDetails.taxCheckStatus === TaxCheckStatusType.review && (
            <Paragraph>- {t(getKey('Проверка подтверждающих документов'))}.</Paragraph>
          )}
        </Margin>
      )}

      <Divider />

      <FormItem
        name={'innText'}
        label={innLabel}
        normalize={(value) => value.replace(/[^\d]/g, '')}
        extra={
          selectedCountry === Country.GE ? t(getSharedKey('maxLength'), { count: companyDetails.innMaxLength }) : ''
        }
        rules={[
          { required: true, message: t(getSharedKey('required')) },
          {
            min: selectedCountry === Country.GE  ? 1 : companyDetails.innMinLength,
            message: t(getSharedKey('minLength'), { count: companyDetails.innMinLength }),
          },
          {
            validator: (_, value) => {
              if (
                selectedCountry === Country.UZ &&
                value.length > companyDetails.innMinLength &&
                !companyDetails.isValidInn
              ) {
                return Promise.reject(
                  new Error(
                    t(getKey('Поле должно состоять'), {
                      min: companyDetails.innMinLength,
                      max: companyDetails.innMaxLength,
                    }),
                  ),
                )
              }
              return Promise.resolve()
            },
          },
        ]}
      >
        <Input
          disabled={isEdit}
          value={companyDetails.innText}
          onChange={(e) => handleInnChange(e.target.value)}
          maxLength={companyDetails.innMaxLength}
          placeholder={getInnPlaceholder()}
        />
      </FormItem>

      {companyDetails.loadingCompanyInfo && <Spinner />}

      {companyDetails.isValidInn && !companyDetails.loadingCompanyInfo && (
        <>
          <FormItem
            label={t(getKey('Название компании'))}
            name={'companyName'}
            rules={[{ required: true, message: t(getSharedKey('required')) }]}
          >
            <Input
              value={companyDetails.companyName}
              onChange={(e) => companyDetails.setCompanyName(e.target.value)}
              placeholder={accountPlacehorders[selectedCountry].companyName}
            />
          </FormItem>

          <FormItem
            label={t(getKey('Наименование банка'))}
            name={'bankName'}
            rules={[{ required: true, message: t(getSharedKey('required')) }]}
          >
            <Input
              value={companyDetails.bankName}
              onChange={(e) => companyDetails.setBankName(e.target.value)}
              placeholder={accountPlacehorders[selectedCountry].bankName}
            />
          </FormItem>

          {companyDetails.selectedCountry.country === Country.BY && (
            <>
              <FormItem
                label={t(getKey('Юридический адрес'))}
                name={'legalAddress'}
                rules={[{ required: true, message: t(getSharedKey('required')) }]}
              >
                <Input
                  value={companyDetails.legalAddress}
                  onChange={(e) => companyDetails.setLegalAddress(e.target.value)}
                  placeholder={selectedCountry === Country.BY ? accountPlacehorders[selectedCountry].legalAddress : ''}
                />
              </FormItem>
              {isEdit && (
                <FormItem
                  name={'gln'}
                  label={t(getKey('GLN (необязательно)'))}
                  normalize={(value) => value.replace(/[^\d]/g, '')}
                  rules={[
                    {
                      min: accountDetails.BY.gln,
                      message: t(getSharedKey('minLength'), { count: accountDetails.BY.gln }),
                    },
                  ]}
                >
                  <Input
                    value={companyDetails.gln}
                    onChange={(e) => companyDetails.setGln(e.target.value)}
                    maxLength={accountDetails.BY.gln}
                    placeholder={selectedCountry === Country.BY ? accountPlacehorders[selectedCountry].gln : ''}
                  />
                </FormItem>
              )}
            </>
          )}

          {isBikOrPayAccountInvalid && (
            <>
              <Divider />
              <Paragraph>{t(getKey('Проверьте БИК и Р/с'))}.</Paragraph>
              <Divider />
            </>
          )}

          <FormItem
            label={t(getKey('БИК'))}
            name={'bik'}
            normalize={(value) => bikNormalize(value)}
            rules={[
              { required: true, message: t(getSharedKey('required')) },
              {
                min: companyDetails.bikMinLength,
                message: t(getSharedKey('minLength'), { count: companyDetails.bikMinLength }),
              },
              {
                validator: (_, value) => {
                  if (value.length > companyDetails.bikMinLength && companyDetails.isBikInvalid) {
                    return Promise.reject(
                      new Error(
                        t(getKey('Поле должно состоять'), {
                          min: companyDetails.bikMinLength,
                          max: companyDetails.bikMaxLength,
                        }),
                      ),
                    )
                  }
                  return Promise.resolve()
                },
              },
            ]}
          >
            <Input
              disabled={companyDetails.dadataMap.bik}
              value={companyDetails.bik}
              onChange={(e) => companyDetails.setBik(e.target.value)}
              maxLength={companyDetails.bikMaxLength}
              placeholder={accountPlacehorders[selectedCountry].bik}
            />
          </FormItem>

          <FormItem
            label={t(getKey('Номер счета получателя'))}
            name={'payAccount'}
            normalize={(value) => normalizePayAccount(value)}
            rules={[
              { required: true, message: t(getSharedKey('required')) },
              {
                min: companyDetails.payAccountMinLength,
                message: t(getSharedKey('minLength'), { count: companyDetails.payAccountMinLength }),
              },
            ]}
          >
            <Input
              value={companyDetails.payAccount}
              onChange={(e) => companyDetails.setPayAccount(e.target.value)}
              maxLength={companyDetails.payAccountMaxLength}
              placeholder={accountPlacehorders[selectedCountry].payAccount}
            />
          </FormItem>

          {companyDetails.selectedCountry.country === Country.RU && (
            <FormItem
              label={t(getKey('Корреспондентский счёт банка получателя'))}
              name={'corAccount'}
              normalize={(value) => value.replace(/[^\d]/g, '')}
              rules={[
                { required: true, message: t(getSharedKey('required')) },
                {
                  min: companyDetails.corAccountMaxLength,
                  message: t(getSharedKey('minLength'), { count: companyDetails.corAccountMaxLength }),
                },
              ]}
            >
              <Input
                value={companyDetails.corAccount}
                onChange={(e) => companyDetails.setCorAccount(e.target.value)}
                maxLength={companyDetails.corAccountMaxLength}
                placeholder={selectedCountry === Country.RU ? accountPlacehorders[selectedCountry].corAccount : ''}
              />
            </FormItem>
          )}
        </>
      )}

      <Divider />

      {/* <Text>
        t(getKey('Если вы ещё не успели')){' '}
        <Href href={PromoRoutesEnum.bankingServices} type="text">
          t(getKey('предложения наших банков-партнеров'))
        </Href>
      </Text> */}

      <Margin mt={44} mb={40}>
        {!isGeDomain() && selectedCountry !== Country.GE && (
          // {selectedCountry !== Country.GE && (
          <FormItem>
            <Checkbox
              onClick={() => companyDetails.setAcceptPersonalInfo()}
              checked={companyDetails.acceptPersonalInfo}
            >
              {t(getKey('Согласен с'))}
              <Href href={URLS.privacyPolicy} linkType="search" type="corp" target="_blank">
                {` ${t(getKey('Политикой Вайлдберриз'))} `}
              </Href>
              {t(getKey('в отношении организации обработки'))}
            </Checkbox>
          </FormItem>
        )}
      </Margin>
    </Form>
  )
})
